class LinkedCollectionOptions extends HTMLElement {
  // Shared across instances so league + team selects (and SPA re-renders) reuse one fetch
  static siblingCache = new Map();
  static metaobjectCache = new Map();

  async connectedCallback() {
    this._type = this.dataset.type;
    this._shop = this.dataset.shop;
    this._token = this.dataset.token;
    this._collectionId = this.dataset.collectionId;
    this._currentTeam = this.dataset.currentTeam || '';
    this._currentLeague = this.dataset.currentLeague || '';
    this._currentColor = this.dataset.currentColor || '';
    this._leagueRules = this.hasAttribute('data-league-rules');

    this._fancySelect = this.previousElementSibling;

    if (!this._shop || !this._token || !this._collectionId || this._fancySelect?.tagName !== 'FANCY-SELECT') {
      this.remove();
      return;
    }

    // wait for fancy-select to load before using its API
    await customElements.whenDefined('fancy-select');

    if (typeof this._fancySelect.addOptions !== 'function') {
      this.remove();
      return;
    }

    this._load();
  }

  async _load() {
    try {
      const siblings = await this._fetchSiblings();
      if (this._type === 'league') {
        await this._loadLeagues(siblings);
      } else {
        await this._loadTeams(siblings);
      }
    } catch (err) {
      console.error('[linked-collection-options] fetch error:', err);
    } finally {
      this.remove();
    }
  }

  async _request(query, variables = {}) {
    const res = await fetch(`https://${this._shop}/api/2024-10/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this._token,
      },
      body: JSON.stringify({ query, variables }),
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const { data, errors } = await res.json();
    if (errors?.length) throw new Error(errors[0].message);

    return data;
  }

  _fetchSiblings() {
    const cache = LinkedCollectionOptions.siblingCache;
    if (cache.has(this._collectionId)) return cache.get(this._collectionId);

    const query = `query LinkedSiblings($id: ID!, $cursor: String) {
      collection(id: $id) {
        products(first: 250, after: $cursor) {
          pageInfo {
            hasNextPage
            endCursor
          }
          nodes {
            handle
            tags
          }
        }
      }
    }`;

    const promise = (async () => {
      const allNodes = [];
      let cursor = null;
      let page = 0;
      const MAX_PAGES = 20;

      do {
        const data = await this._request(query, {
          id: `gid://shopify/Collection/${this._collectionId}`,
          cursor,
        });

        const connection = data?.collection?.products;
        allNodes.push(...(connection?.nodes ?? []));
        cursor = connection?.pageInfo?.hasNextPage ? connection.pageInfo.endCursor : null;
        page++;
      } while (cursor && page < MAX_PAGES);

      return allNodes.map(node => ({
        handle: node.handle,
        tags: node.tags.map(tag => tag.toLowerCase()),
      }));
    })();

    promise.catch(() => cache.delete(this._collectionId));
    cache.set(this._collectionId, promise);
    return promise;
  }

  // Batches metaobject lookups as GraphQL aliases, 50 per request
  async _fetchMetaobjects(type, handles) {
    const cache = LinkedCollectionOptions.metaobjectCache;
    const missing = handles.filter(handle => !cache.has(`${type}:${handle}`));
    const CHUNK_SIZE = 50;

    const chunks = [];
    for (let i = 0; i < missing.length; i += CHUNK_SIZE) {
      chunks.push(missing.slice(i, i + CHUNK_SIZE));
    }

    await Promise.all(chunks.map(async chunk => {
      const query = `query LinkedMetaobjects {
        ${chunk.map((handle, i) => `m${i}: metaobject(handle: { type: ${JSON.stringify(type)}, handle: ${JSON.stringify(handle)} }) {
          name: field(key: "name") {
            value
          }
          logo: field(key: "logo") {
            reference {
              ... on MediaImage {
                image {
                  url(transform: { maxWidth: 80 })
                }
              }
            }
          }
        }`).join('\n')}
      }`;

      const data = await this._request(query);

      chunk.forEach((handle, i) => {
        const node = data?.[`m${i}`];
        cache.set(`${type}:${handle}`, {
          name: node?.name?.value ?? null,
          logo: node?.logo?.reference?.image?.url ?? null,
        });
      });
    }));

    return new Map(handles.map(handle => [handle, cache.get(`${type}:${handle}`)]));
  }

  async _loadLeagues(siblings) {
    const leagues = this._uniqueTags(siblings, 'league:');
    this._setVisible(leagues.length > 1);

    const entries = leagues
      .map((league, index) => {
        if (league === this._currentLeague) return null;
        const destination = siblings.find(sibling => sibling.tags.includes(league));
        if (!destination) return null;
        return {
          handle: this._handleize(league.replace('league:', '')),
          value: `/products/${destination.handle}`,
          index,
        };
      })
      .filter(Boolean);

    if (!entries.length) return;

    const meta = await this._fetchMetaobjects('sparky--league', entries.map(entry => entry.handle));

    this._fancySelect.addOptions(entries.map(entry => {
      const league = meta.get(entry.handle);
      return {
        value: entry.value,
        text: (league?.name || entry.handle.replace(/-/g, ' ')).toUpperCase(),
        image: league?.logo ?? null,
        index: entry.index,
      };
    }));
  }

  async _loadTeams(siblings) {
    const inLeague = this._currentLeague
      ? siblings.filter(sibling => sibling.tags.includes(this._currentLeague))
      : siblings;
    const teams = this._uniqueTags(inLeague, 'team:');

    if (this._leagueRules) {
      const leagueCount = this._uniqueTags(siblings, 'league:').length;
      this._setVisible(teams.length > 1 || (teams.length >= 1 && leagueCount > 1));
      this._fancySelect.setDisabled?.(teams.length <= 1 && leagueCount > 1);
    } else {
      this._setVisible(teams.length > 1);
    }

    const entries = teams
      .map((team, index) => {
        if (team === this._currentTeam) return null;
        const destination = this._teamDestination(siblings, team);
        if (!destination) return null;
        return {
          handle: this._handleize(team.replace('team:', '')),
          value: `/products/${destination.handle}`,
          index,
        };
      })
      .filter(Boolean);

    if (!entries.length) return;

    const meta = await this._fetchMetaobjects('sparky--team', entries.map(entry => entry.handle));

    this._fancySelect.addOptions(entries
      .map(entry => {
        const logo = meta.get(entry.handle)?.logo;
        if (!logo) return null;
        return {
          value: entry.value,
          text: entry.handle.replace(/-/g, ' ').toUpperCase(),
          image: logo,
          index: entry.index,
        };
      })
      .filter(Boolean));
  }

  // First sibling with the team + current color, else first sibling with the team
  _teamDestination(siblings, team) {
    let fallback = null;
    for (const sibling of siblings) {
      if (!sibling.tags.includes(team)) continue;
      fallback ??= sibling;
      if (this._currentColor && sibling.tags.includes(this._currentColor)) return sibling;
    }
    return fallback;
  }

  _uniqueTags(siblings, prefix) {
    const tags = new Set();
    for (const sibling of siblings) {
      for (const tag of sibling.tags) {
        if (tag.includes(prefix)) tags.add(tag);
      }
    }
    return Array.from(tags).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  }

  _handleize(str) {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  _setVisible(visible) {
    this._fancySelect.closest('[data-linked-wrap]')?.classList.toggle('hidden', !visible);
  }
}

if (!customElements.get('linked-collection-options')) {
  customElements.define('linked-collection-options', LinkedCollectionOptions);
}
